import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ArchivePositionInput,
  ChangePositionStatusInput,
  CreateDepartmentInput,
  CreatePositionRequest,
  CreateTeamInput,
  CreateVentureInput,
  PositionStatus,
  ReassignPositionInput,
  SetHeadcountPlanRequest,
  UpdatePositionRequest,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost, request } from '../../../lib/api-client';

// ORG-01 to ORG-04. One file, because the four screens read each other's data —
// the ledger needs the tree's node ids, the tree needs the ledger's counts to stay
// in step, and splitting them would mean four query keys nobody could invalidate
// together.

export interface RollupCounts {
  readonly total: number;
  readonly filled: number;
  readonly vacant: number;
  readonly inRecruitment: number;
  readonly planned: number;
  readonly frozen: number;
}

export type OrgUnitKind = 'venture' | 'department' | 'team';

export interface OrgTreeNode {
  readonly id: string;
  readonly kind: OrgUnitKind;
  readonly name: string;
  readonly code: string | null;
  readonly counts: RollupCounts;
  readonly children: readonly OrgTreeNode[];
}

export interface PositionRow {
  readonly id: string;
  readonly code: string;
  readonly title: string;
  readonly grade: string | null;
  readonly employmentType: string;
  readonly workMode: string;
  readonly location: string | null;
  readonly status: PositionStatus;
  readonly previousStatus: PositionStatus | null;
  readonly totalSlots: number;
  readonly openSlots: number;
  readonly ventureId: string;
  readonly ventureName: string;
  readonly departmentId: string;
  readonly departmentName: string;
  readonly teamId: string | null;
  readonly teamName: string | null;
  readonly hiringManagerUserId: string | null;
  readonly hiringManagerName: string | null;
  readonly frozenAt: string | null;
  readonly frozenReason: string | null;
  readonly archivedAt: string | null;
  readonly archiveNote: string | null;
  readonly version: number;
  readonly createdAt: string;
  readonly vacantSince: string | null;
  readonly daysVacant: number | null;
  /** Null plus a `restrictedFields` entry when §L23 hides the band from the viewer. */
  readonly salaryBandMinMinor: string | null;
  readonly salaryBandMaxMinor: string | null;
  readonly salaryCurrency: string | null;
  readonly restrictedFields?: readonly string[];
}

export interface PositionHistoryEntry {
  readonly occurredAt: string;
  readonly eventType: string;
  readonly actorName: string | null;
  readonly actorType: 'user' | 'system';
  readonly before: Record<string, unknown> | null;
  readonly after: Record<string, unknown> | null;
}

export interface LinkedRequisition {
  readonly id: string;
  readonly reference: string;
  readonly title: string;
  readonly status: string;
  readonly createdAt: string;
  readonly isActive: boolean;
}

export interface PositionDetail extends PositionRow {
  readonly history: readonly PositionHistoryEntry[];
  readonly requisitions: readonly LinkedRequisition[];
}

export interface VentureRow {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly legalName: string | null;
  readonly defaultCurrency: string;
  readonly timezone: string;
  readonly archivedAt: string | null;
  readonly version: number;
}

export interface DepartmentRow {
  readonly id: string;
  readonly ventureId: string;
  readonly name: string;
  readonly code: string | null;
  readonly headUserId: string | null;
  readonly headUserName: string | null;
  readonly archivedAt: string | null;
  readonly version: number;
}

export interface TeamRow {
  readonly id: string;
  readonly departmentId: string;
  readonly name: string;
  readonly leadUserId: string | null;
  readonly leadUserName: string | null;
  readonly archivedAt: string | null;
  readonly version: number;
}

export interface HeadcountPlanRow {
  readonly id: string;
  readonly ventureId: string;
  readonly ventureName: string;
  readonly departmentId: string | null;
  readonly departmentName: string | null;
  readonly teamId: string | null;
  readonly teamName: string | null;
  readonly fiscalYear: number;
  readonly quarter: number;
  readonly plannedHeadcount: number;
  readonly approvedHeadcount: number;
  readonly budgetAmountMinor: string | null;
  readonly budgetCurrency: string | null;
  readonly notes: string | null;
  readonly version: number;
  readonly filledHeadcount: number;
}

// --- Query keys -------------------------------------------------------------
//
// One root, so a position write can invalidate the tree, the ledger and the plan
// grid together — all three show counts that any one write can move.

export const ORG_KEY = ['organisation'] as const;
export const ORG_TREE_KEY = [...ORG_KEY, 'tree'] as const;
export const POSITIONS_KEY = [...ORG_KEY, 'positions'] as const;
export const HEADCOUNT_KEY = [...ORG_KEY, 'headcount-plans'] as const;

export interface PositionListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly query?: string;
  /** One or more statuses, comma-separated: `planned,vacant`. */
  readonly status?: string;
  readonly ventureId?: string;
  readonly departmentId?: string;
  readonly teamId?: string;
  readonly includeArchived?: boolean;
  readonly archivedOnly?: boolean;
  /** Only seats with no request open (the Request a hire picker). */
  readonly requestable?: boolean;
  readonly sortBy?: string;
  readonly sortDirection?: 'asc' | 'desc';
}

export function useOrgTree(): UseQueryResult<readonly OrgTreeNode[], ApiError> {
  return useQuery<readonly OrgTreeNode[], ApiError>({
    queryKey: ORG_TREE_KEY,
    queryFn: () => apiGet<readonly OrgTreeNode[]>('/organisation/tree'),
    // The server caches this for 60s and drops it on every write, so the browser
    // holding it briefly costs nothing and saves a request on every navigation
    // back to a screen S5 says is opened constantly.
    staleTime: 30_000,
  });
}

export function usePositions(params: PositionListParams) {
  return useQuery<{ rows: readonly PositionRow[]; total: number }, ApiError>({
    queryKey: [...POSITIONS_KEY, params],
    queryFn: async () => {
      const result = await request<readonly PositionRow[]>('/positions', { query: { ...params } });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2) — a
    // filter change swaps rows in place rather than dropping to a skeleton.
    placeholderData: (previous) => previous,
  });
}

export function usePosition(id: string | null): UseQueryResult<PositionDetail | null, ApiError> {
  return useQuery<PositionDetail | null, ApiError>({
    queryKey: [...POSITIONS_KEY, 'detail', id],
    queryFn: () => (id ? apiGet<PositionDetail>(`/positions/${id}`) : Promise.resolve(null)),
    enabled: id !== null,
  });
}

export function useVentures(): UseQueryResult<readonly VentureRow[], ApiError> {
  return useQuery<readonly VentureRow[], ApiError>({
    queryKey: [...ORG_KEY, 'ventures'],
    queryFn: () => apiGet<readonly VentureRow[]>('/organisation/ventures'),
    staleTime: 5 * 60_000,
  });
}

export function useDepartments(): UseQueryResult<readonly DepartmentRow[], ApiError> {
  return useQuery<readonly DepartmentRow[], ApiError>({
    queryKey: [...ORG_KEY, 'departments'],
    queryFn: () => apiGet<readonly DepartmentRow[]>('/organisation/departments'),
    staleTime: 5 * 60_000,
  });
}

export function useTeams(): UseQueryResult<readonly TeamRow[], ApiError> {
  return useQuery<readonly TeamRow[], ApiError>({
    queryKey: [...ORG_KEY, 'teams'],
    queryFn: () => apiGet<readonly TeamRow[]>('/organisation/teams'),
    staleTime: 5 * 60_000,
  });
}

export interface HeadcountPlanParams {
  readonly fiscalYear?: number;
  readonly ventureId?: string;
  readonly departmentId?: string;
  readonly teamId?: string;
}

export function useHeadcountPlans(
  params: HeadcountPlanParams,
): UseQueryResult<readonly HeadcountPlanRow[], ApiError> {
  return useQuery<readonly HeadcountPlanRow[], ApiError>({
    queryKey: [...HEADCOUNT_KEY, params],
    queryFn: async () => {
      const result = await request<readonly HeadcountPlanRow[]>('/organisation/headcount-plans', {
        query: { ...params },
      });
      return result.data;
    },
    placeholderData: (previous) => previous,
  });
}

// --- Mutations --------------------------------------------------------------
//
// Every one invalidates the whole `organisation` root and awaits it. CLAUDE.md
// §5a rule 3: releasing the optimistic state before the refetch lands re-renders
// from the stale cache, so the row visibly jumps back and then forward again.

function useOrgMutation<TResult, TInput>(run: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: run,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ORG_KEY });
    },
  });
}

export function useCreatePosition() {
  return useOrgMutation<PositionRow, CreatePositionRequest>((input) =>
    apiPost<PositionRow>('/positions', input),
  );
}

export function useUpdatePosition() {
  return useOrgMutation<PositionRow, { id: string } & UpdatePositionRequest>(({ id, ...input }) =>
    apiPatch<PositionRow>(`/positions/${id}`, input),
  );
}

export function useChangePositionStatus() {
  return useOrgMutation<PositionRow, { id: string } & ChangePositionStatusInput>(
    ({ id, ...input }) => apiPost<PositionRow>(`/positions/${id}/status`, input),
  );
}

export function useUnfreezePosition() {
  return useOrgMutation<PositionRow, { id: string; version: number }>(({ id, version }) =>
    apiPost<PositionRow>(`/positions/${id}/unfreeze`, { version }),
  );
}

export function useReassignPosition() {
  return useOrgMutation<PositionRow, { id: string } & ReassignPositionInput>(({ id, ...input }) =>
    apiPost<PositionRow>(`/positions/${id}/reassign`, input),
  );
}

export function useArchivePosition() {
  return useOrgMutation<PositionRow, { id: string } & ArchivePositionInput>(({ id, ...input }) =>
    apiPost<PositionRow>(`/positions/${id}/archive`, input),
  );
}

export function useRestorePosition() {
  return useOrgMutation<PositionRow, { id: string; version: number }>(({ id, version }) =>
    apiPost<PositionRow>(`/positions/${id}/restore`, { version }),
  );
}

export function useCreateVenture() {
  return useOrgMutation<VentureRow, CreateVentureInput>((input) =>
    apiPost<VentureRow>('/organisation/ventures', input),
  );
}

export function useCreateDepartment() {
  return useOrgMutation<DepartmentRow, CreateDepartmentInput>((input) =>
    apiPost<DepartmentRow>('/organisation/departments', input),
  );
}

export function useCreateTeam() {
  return useOrgMutation<TeamRow, CreateTeamInput>((input) =>
    apiPost<TeamRow>('/organisation/teams', input),
  );
}

export function useArchiveOrgUnit() {
  return useOrgMutation<
    { id: string; archived: true },
    { kind: OrgUnitKind; id: string; version: number; note?: string }
  >(({ kind, id, version, note }) =>
    apiPost<{ id: string; archived: true }>(`/organisation/${kind}/${id}/archive`, {
      version,
      ...(note ? { note } : {}),
    }),
  );
}

export function useSetHeadcountPlan() {
  return useOrgMutation<HeadcountPlanRow, SetHeadcountPlanRequest>((input) =>
    apiPost<HeadcountPlanRow>('/organisation/headcount-plans', input),
  );
}

/** The ledger export, as a download the browser saves rather than a page it opens. */
export function positionExportUrl(params: PositionListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== false) search.set(key, String(value));
  }
  return `/api/v1/positions/export?${search.toString()}`;
}
